import { useCallback, useRef } from "react";
// eslint-disable-next-line no-restricted-imports
import { resolvePath, useLocation, useNavigate } from "react-router";

import { trackException } from "@bokio/utils/t";
import { getCurrentRoute, withCurrentReturnUrl } from "@bokio/utils/url";

import type { Location, To } from "react-router";

/**
 * Workaround for allowing Jest to spy on window.location.href set, which is not doable in JSDOM 26.
 * https://github.com/jsdom/jsdom/issues/3492
 */
export const locationHrefSetImplementation = {
	current: (href: string) => {
		window.location.href = href;
	},
};

// Explicitly declared route state types.
// Prefer optional types so we don't have to explicitly declare everything when we only care about one of the props.
export interface BokioRouterLocationState {
	isRedirectedFromNotification?: boolean;
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	signUpSceneProps?: any;
	notFound?: boolean;
	error?: unknown;
}

export type RouterProps = {
	push: (path: To, state?: BokioRouterLocationState) => void;
	replace: (path: To, state?: BokioRouterLocationState) => void;
	goBack: () => void;
	goForward: () => void;
	location: Location<BokioRouterLocationState>;
	redirect: (path: To, state?: BokioRouterLocationState) => void;
	redirectHard: (path: To) => void;

	currentCompanyId: string | undefined;
	currentAgencyId: string | undefined;
};

/**
 * Tips for unit testing:
 * See the `history` prop from `renderApp(...)` for example of testing.
 */
export const useRouter = (): RouterProps => {
	const navigate = useNavigate();
	const location = useLocation();

	const routerRef = useRef({} as unknown as RouterProps);

	// eslint-disable-next-line react-hooks/refs -- SS 2025-10-13 This lint error should be fixed in the future. Existing usages were disabled just to simplify merging lint rules upgrade.
	routerRef.current.goBack = useCallback(() => navigate(-1), [navigate]);

	// eslint-disable-next-line react-hooks/refs -- SS 2025-10-13 This lint error should be fixed in the future. Existing usages were disabled just to simplify merging lint rules upgrade.
	routerRef.current.goForward = useCallback(() => navigate(1), [navigate]);

	// eslint-disable-next-line react-hooks/refs -- SS 2025-10-13 This lint error should be fixed in the future. Existing usages were disabled just to simplify merging lint rules upgrade.
	routerRef.current.location = location;

	// eslint-disable-next-line react-hooks/refs -- SS 2025-10-13 This lint error should be fixed in the future. Existing usages were disabled just to simplify merging lint rules upgrade.
	routerRef.current.push = useCallback(
		(to: To, state?: BokioRouterLocationState) =>
			navigate(to, {
				state,
			}),
		[navigate],
	);

	// eslint-disable-next-line react-hooks/refs -- SS 2025-10-13 This lint error should be fixed in the future. Existing usages were disabled just to simplify merging lint rules upgrade.
	routerRef.current.redirect = useCallback(
		(to: To, state?: BokioRouterLocationState) => {
			const nextRoute = withCurrentReturnUrl(location, getCurrentRoute(resolvePath(to)));
			navigate(nextRoute, {
				state,
			});
		},
		[location, navigate],
	);

	// eslint-disable-next-line react-hooks/refs -- SS 2025-10-13 This lint error should be fixed in the future. Existing usages were disabled just to simplify merging lint rules upgrade.
	routerRef.current.redirectHard = useCallback(
		(to: To) => {
			// SS 2025-02-20: Keeping the legacy behaviour where people previously passed a full URL to this function, and expected this function to link to somewhere outside of the web app.
			const isRedirectingToExternalLink = typeof to === "string" && /^(?:https?:)?\/\//i.test(to);

			locationHrefSetImplementation.current(
				isRedirectingToExternalLink ? to : withCurrentReturnUrl(location, getCurrentRoute(resolvePath(to))),
			);
		},
		[location],
	);

	// eslint-disable-next-line react-hooks/refs -- SS 2025-10-13 This lint error should be fixed in the future. Existing usages were disabled just to simplify merging lint rules upgrade.
	routerRef.current.replace = useCallback(
		(to: To, state?: BokioRouterLocationState) => {
			try {
				navigate(to, {
					state,
					replace: true,
				});
			} catch (err) {
				trackException(err, "withRouterControls", { location });
			}
		},
		[location, navigate],
	);

	// eslint-disable-next-line react-hooks/refs -- SS 2025-10-13 This lint error should be fixed in the future. Existing usages were disabled just to simplify merging lint rules upgrade.
	routerRef.current.currentCompanyId = location.pathname.match(
		/^\/([0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12})/,
	)?.[1];
	// eslint-disable-next-line react-hooks/refs -- SS 2025-10-13 This lint error should be fixed in the future. Existing usages were disabled just to simplify merging lint rules upgrade.
	routerRef.current.currentAgencyId = location.pathname.match(
		/^\/(?:backoffice|agencies)\/([0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12})/,
	)?.[1];

	// eslint-disable-next-line react-hooks/refs -- SS 2025-10-13 This lint error should be fixed in the future. Existing usages were disabled just to simplify merging lint rules upgrade.
	return routerRef.current;
};
